import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    Image,
    View
} from "react-native";
import type {
    MemberDataType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    PageContainer,
    TextInput,
    Seperator,
    Button,
    Switch,
    Modal,
    Chip,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import type {
    NativeStackNavigationProp,
    NativeStackScreenProps
} from "@react-navigation/native-stack";
import {
    ArrowLeftIcon,
    TrashIcon,
    PenIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";
import MemberEditModal from "../editModal";

type Props = NativeStackScreenProps<RootStackParamList, "MemberDetail">;

const MemberDetail = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const route = useRoute<Props["route"]>();
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const userID = route.params?.userID;

    const [
        memberData,
        setMemberData
    ] = useState<MemberDataType | null>(null);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        isEditModalVisible,
        setIsEditModalVisible
    ] = useState<boolean>(false);

    const [
        isDeleteDialogOpen,
        setIsDeleteDialogOpen
    ] = useState<boolean>(false);

    const [
        deleteConfirmationText,
        setDeleteConfirmationText
    ] = useState<string>("");

    const [
        isBlacklistEnabled,
        setIsBlacklistEnabled
    ] = useState<boolean>(false);

    const [
        blacklistReason,
        setBlacklistReason
    ] = useState<string>("");

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const fetchMemberDetail = useCallback(async () => {
        if(!userID) {
            return;
        }

        try {
            setLoading(true);
            const result = await RESTServiceContext.action("getUserForAdmin", {
                userID: userID
            });
            if(result.payload) {
                setMemberData(result.payload as MemberDataType);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [
        userID
    ]);

    useEffect(() => {
        fetchMemberDetail();
    }, [
        fetchMemberDetail
    ]);

    const handleDeleteUser = async () => {
        try {
            const result = await RESTServiceContext.action("deleteUser", {
                accountDeleteReason: isBlacklistEnabled ? blacklistReason : undefined,
                isBlacklisted: isBlacklistEnabled,
                userID: userID
            });
            if(result.code === 200 || result.payload) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-delete-success") || "Üye başarıyla silindi."
                });
                navigation.goBack();
            } else {
                NCoreUIKitSnackBar.open({
                    title: localize("members-delete-error") || "Üye silinirken hata oluştu."
                });
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("members-delete-error") || "Üye silinirken hata oluştu."
            });
        } finally {
            setIsDeleteDialogOpen(false);
        }
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingXl
                }
            ]}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: spaces.spacingSm
                }}
            >
                <Button
                    onPress={() => navigation.navigate("MembersList")}
                    icon={() => <ArrowLeftIcon
                        color={colors.project.brandWhite}
                        size={24}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
                <Text
                    variant="titleLargeSize"
                >
                    {localize("members-detail-title") || "Kullanıcı Detayı"}
                </Text>
            </View>
            <View
                style={{
                    flexDirection: "row",
                    gap: spaces.spacingSm
                }}
            >
                <Button
                    title={localize("members-edit") || "Düzenle"}
                    onPress={() => setIsEditModalVisible(true)}
                    icon={({
                        color,
                        size
                    }) => <PenIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
            </View>
        </View>;
    };

    if(loading) {
        return <PageContainer>
            <View
                style={{
                    padding: spaces.spacingXl,
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1
                }}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>
        </PageContainer>;
    }

    if(!memberData) {
        return <PageContainer>
            <View
                style={{
                    padding: spaces.spacingXl
                }}
            >
                <Text>{localize("members-detail-user-not-found") || "Kullanıcı bulunamadı."}</Text>
            </View>
        </PageContainer>;
    }

    const initials = `${memberData.user?.firstName?.charAt(0) || ""}${memberData.user?.lastName?.charAt(0) || ""}`.toUpperCase();

    const renderCompanyCard = (
        company: {
            companyPhotoPath?: string;
            foundationYear?: number;
            positionName?: unknown;
            taxOffice?: string;
            taxNumber?: string;
            address?: string;
            title?: string;
            _id?: string;
        },
        keyVal: number | string
    ) => {
        const compInitials = (company.title || "").slice(0, 2).toUpperCase();

        return <TouchableOpacity
            style={[
                stylesheet.companyCardContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    flexBasis: isMobile ? "100%" : 320,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingMd,
                    gap: spaces.spacingMd,
                    flexShrink: 1,
                    flexGrow: 1
                }
            ]}
            onPress={() => {
                if (company._id) {
                    navigation.navigate("CompanyDetail", {
                        companyID: company._id
                    });
                }
            }}
            activeOpacity={0.7}
            key={keyVal}
        >
            <View
                style={[
                    stylesheet.companyCardAvatar,
                    {
                        backgroundColor: colors.project.brandSurface,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.full
                    }
                ]}
            >
                {
                    company.companyPhotoPath ?
                        <Image
                            style={stylesheet.companyCardAvatarImage}
                            source={{
                                uri: company.companyPhotoPath
                            }}
                            resizeMode="cover"
                        />
                    :
                        <Text
                            style={{
                                color: colors.project.gold
                            }}
                            variant="titleMediumSize"
                        >
                            {compInitials}
                        </Text>
                }
            </View>
            <View
                style={[
                    stylesheet.companyCardInfo,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {company.title || localize("members-detail-no-company-name") || "Firma Adı Yok"}
                </Text>
                {
                    (() => {
                        let posName = "-";
                        if (company.positionName) {
                            if (typeof company.positionName === "object") {
                                const positionRecord = company.positionName as Record<string, string>;
                                posName = positionRecord.tr || positionRecord.en || "-";
                            } else {
                                posName = company.positionName as string;
                            }
                        }

                        if (posName !== "-") {
                            return <Text
                                style={{
                                    color: colors.content.text.mid
                                }}
                                variant="bodySmallSize"
                            >
                                {localize("members-detail-position") || "Pozisyon:"} {posName}
                            </Text>;
                        }

                        return null;
                    })()
                }
                {
                    company.foundationYear ?
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-foundation") || "Kuruluş Yılı:"} {company.foundationYear}
                        </Text>
                    :
                        null
                }
                <Text
                    style={{
                        color: colors.content.text.mid
                    }}
                    variant="bodySmallSize"
                >
                    {company.taxOffice ? `${localize("members-detail-tax-office") || "Vergi Dairesi:"} ${company.taxOffice} • ` : ""}{localize("members-detail-tax-number") || "Vergi No:"} {company.taxNumber || "-"}
                </Text>
                {
                    company.address ?
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-address") || "Adres:"} {company.address}
                        </Text>
                    :
                        null
                }
            </View>
        </TouchableOpacity>;
    };

    return <PageContainer
        scrollViewStyle={[
            stylesheet.container,
            {
                padding: isMobile ? spaces.spacingMd : spaces.spacingXl,
                gap: isMobile ? spaces.spacingMd : spaces.spacingXl
            }
        ]}
        isScrollable={true}
    >
        {renderHeader()}
        <View
            style={[
                stylesheet.profileCard,
                {
                    padding: isMobile ? spaces.spacingMd : spaces.spacingXl,
                    gap: isMobile ? spaces.spacingMd : spaces.spacingXl,
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.lg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.profileHeader,
                    {
                        alignItems: isMobile ? "flex-start" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "space-between",
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={{
                        alignItems: isMobile ? "flex-start" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        width: isMobile ? "100%" : undefined,
                        gap: spaces.spacingLg,
                        flex: 1
                    }}
                >
                    <View
                        style={[
                            stylesheet.avatarContainer,
                            {
                                backgroundColor: colors.project.brandSurface,
                                borderColor: colors.project.brandBorder,
                                borderWidth: borders.seperator,
                                borderRadius: radiuses.full,
                                height: isMobile ? 80 : 120,
                                width: isMobile ? 80 : 120
                            }
                        ]}
                    >
                        {
                            memberData.user?.profilePhotoPath ?
                                <Image
                                    source={{
                                        uri: memberData.user.profilePhotoPath
                                    }}
                                    style={stylesheet.avatarImage}
                                    resizeMode="cover"
                                />
                            :
                                <Text
                                    variant={isMobile ? "titleLargeSize" : "displaySmallSize"}
                                    style={{
                                        color: colors.project.gold
                                    }}
                                >
                                    {initials}
                                </Text>
                        }
                    </View>
                    <View
                        style={[
                            stylesheet.infoContainer,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <View
                            style={{
                                alignItems: "center",
                                flexDirection: "row",
                                gap: spaces.spacingMd
                            }}
                        >
                            <Text
                                variant="displaySmallSize"
                            >
                                {memberData.user?.firstName} {memberData.user?.lastName}
                            </Text>
                            <Chip
                                title={memberData.user?.isActive ? localize("members-detail-active") || "Aktif" : localize("members-detail-passive") || "Pasif"}
                                type={memberData.user?.isActive ? "success" : "danger"}
                                spreadBehaviour="free"
                                isClosable={false}
                                size="small"
                            />
                        </View>
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="titleMediumSize"
                        >
                            {memberData.user?.mail}
                        </Text>
                    </View>
                </View>
                {
                    memberData.companies && memberData.companies.length > 0 ?
                        (() => {
                            const activeCompany = memberData.companies[0];
                            if (!activeCompany) {
                                return null;
                            }

                            const companyInitials = (activeCompany.title || "").slice(0, 2).toUpperCase();

                            return <TouchableOpacity
                                style={[
                                    stylesheet.summaryCompanyCardContainer,
                                    {
                                        backgroundColor: colors.project.brandSurface,
                                        borderColor: colors.project.brandBorder,
                                        width: isMobile ? "100%" : undefined,
                                        borderWidth: borders.seperator,
                                        borderRadius: radiuses.md,
                                        padding: spaces.spacingMd,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                                onPress={() => {
                                    navigation.navigate("CompanyDetail", {
                                        companyID: activeCompany._id
                                    });
                                }}
                                activeOpacity={0.7}
                            >
                                <View
                                    style={[
                                        stylesheet.summaryCompanyCardAvatar,
                                        {
                                            backgroundColor: colors.project.brandWhite,
                                            borderColor: colors.project.brandBorder,
                                            borderWidth: borders.seperator,
                                            borderRadius: radiuses.full
                                        }
                                    ]}
                                >
                                    {
                                        activeCompany.companyPhotoPath ?
                                            <Image
                                                style={stylesheet.summaryCompanyCardAvatarImage}
                                                source={{
                                                    uri: activeCompany.companyPhotoPath
                                                }}
                                                resizeMode="cover"
                                            />
                                        :
                                            <Text
                                                style={{
                                                    color: colors.project.gold
                                                }}
                                                variant="titleMediumSize"
                                            >
                                                {companyInitials}
                                            </Text>
                                    }
                                </View>
                                <View
                                    style={[
                                        stylesheet.summaryCompanyCardInfo,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <Text
                                        variant="titleMediumSize"
                                    >
                                        {activeCompany.title}
                                    </Text>
                                    {
                                        (() => {
                                            let posName = "-";
                                            if (activeCompany.positionName) {
                                                if (typeof activeCompany.positionName === "object") {
                                                    const positionRecord = activeCompany.positionName as Record<string, string>;
                                                    posName = positionRecord.tr || positionRecord.en || "-";
                                                } else {
                                                    posName = activeCompany.positionName as string;
                                                }
                                            }

                                            if (posName !== "-") {
                                                return <Text
                                                    style={{
                                                        color: colors.content.text.mid
                                                    }}
                                                    variant="bodySmallSize"
                                                >
                                                    {posName}
                                                </Text>;
                                            }

                                            return null;
                                        })()
                                    }
                                </View>
                            </TouchableOpacity>;
                        })()
                    :
                        null
                }
            </View>
            <Seperator
                color="subtle"
            />
            <View
                style={[
                    stylesheet.detailSection,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("members-detail-contact-info")}
                </Text>
                <View
                    style={[
                        stylesheet.detailRow,
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-phone-number") || "Telefon Numarası"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.user?.phoneNumber || "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-tc-no") || "TC Kimlik No"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.metadata?.tcNo || "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-member-code") || "Üye Kodu"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.user?.memberCode || "-"}
                        </Text>
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.detailRow,
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-is-student") || "Öğrenci mi?"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.user?.isStudent ? localize("members-detail-yes") || "Evet" : localize("members-detail-no") || "Hayır"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-birth-date") || "Doğum Tarihi"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.metadata?.birthDate ? new Date(memberData.metadata.birthDate).toLocaleDateString("tr-TR") : "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-registration-date") || "Kayıt Tarihi"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.user?.registrationDate ? new Date(memberData.user.registrationDate).toLocaleDateString("tr-TR") : "-"}
                        </Text>
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.detailRow,
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-blood-type") || "Kan Grubu"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.metadata?.bloodType || "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-marital-status") || "Medeni Durum"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.metadata?.maritalStatus || "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-gender") || "Cinsiyet"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.metadata?.gender || "-"}
                        </Text>
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.detailRow,
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-birth-place") || "Doğum Yeri"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.metadata?.birthPlace || "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-mother-name") || "Anne Adı"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.metadata?.motherName || "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-father-name") || "Baba Adı"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.metadata?.fatherName || "-"}
                        </Text>
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.detailRow,
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-detail-about-trade") || "Mesleğim Hakkında"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {memberData.user?.aboutMyTrade || "-"}
                        </Text>
                    </View>
                </View>
            </View>
            <Seperator
                color="subtle"
            />
            <View
                style={[
                    stylesheet.detailSection,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("members-detail-company-info") || "Firma Bilgileri"}
                </Text>
                {
                    memberData.companies && memberData.companies.length > 0 ?
                        <View
                            style={[
                                stylesheet.companiesListContainer,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            {memberData.companies.map((company, index: number) => renderCompanyCard(company, index))}
                        </View>
                    :
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodyMediumSize"
                        >
                            {localize("members-detail-no-company-info") || "Firma bilgisi bulunmuyor."}
                        </Text>
                }
            </View>
            <Seperator
                color="subtle"
            />
            <View
                style={[
                    stylesheet.detailSection,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("members-detail-address-info") || "Adres Bilgileri"}
                </Text>
                {
                    memberData.metadata?.homeAddress ?
                        <View
                            style={{
                                backgroundColor: colors.project.brandSurface,
                                padding: spaces.spacingMd,
                                borderRadius: radiuses.md
                            }}
                        >
                            {
                                (memberData.metadata.homeAddress.districtName || memberData.metadata.homeAddress.cityName) ?
                                    <Text
                                        style={{
                                            marginBottom: spaces.spacingXs
                                        }}
                                        variant="titleSmallSize"
                                    >
                                        {memberData.metadata.homeAddress.districtName || "-"}{memberData.metadata.homeAddress.cityName ? ` / ${memberData.metadata.homeAddress.cityName}` : ""}
                                    </Text>
                                :
                                    null
                            }
                            <Text
                                variant="bodyMediumSize"
                            >
                                {memberData.metadata.homeAddress.addressDetail || localize("members-detail-no-address-detail") || "Adres detayı yok"}
                            </Text>
                        </View>
                    :
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodyMediumSize"
                        >
                            {localize("members-detail-no-address-info") || "Adres bilgisi bulunmuyor."}
                        </Text>
                }
            </View>
            <Seperator
                color="subtle"
            />
            <View
                style={[
                    stylesheet.detailSection,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("members-detail-education-info") || "Eğitim Bilgileri"}
                </Text>
                {
                    memberData.metadata?.education && memberData.metadata.education.length > 0 ?
                        memberData.metadata.education.map((edu, index: number) => {
                            return <View
                                style={{
                                    backgroundColor: colors.project.brandSurface,
                                    padding: spaces.spacingMd,
                                    borderRadius: radiuses.md
                                }}
                                key={index}
                            >
                                <Text
                                    style={{
                                        marginBottom: spaces.spacingXs
                                    }}
                                    variant="titleSmallSize"
                                >
                                    {edu.universityName || localize("members-detail-no-university") || "Üniversite Belirtilmemiş"}
                                </Text>
                                <Text
                                    style={{
                                        marginBottom: spaces.spacingXs,
                                        color: colors.content.text.mid
                                    }}
                                    variant="bodyMediumSize"
                                >
                                    {edu.facultyName || localize("members-detail-no-faculty") || "Fakülte Belirtilmemiş"} • {edu.departmentName || localize("members-detail-no-department") || "Bölüm Belirtilmemiş"}
                                </Text>
                                <Text
                                    style={{
                                        color: colors.content.text.mid
                                    }}
                                    variant="bodySmallSize"
                                >
                                    {localize("members-detail-graduation-year") || "Mezuniyet Yılı:"} {edu.graduationYear || "-"}
                                </Text>
                            </View>;
                        })
                    :
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodyMediumSize"
                        >
                            {localize("members-detail-no-education-info") || "Eğitim bilgisi bulunmuyor."}
                        </Text>
                }
            </View>
        </View>
        <View
            style={{
                marginTop: spaces.spacingXl,
                alignItems: "center"
            }}
        >
            <Button
                icon={({
                    color,
                    size
                }) => <TrashIcon
                    color={colors.content.icon[color as never] || color}
                    size={size}
                />}
                onPress={() => setIsDeleteDialogOpen(true)}
                title={localize("members-delete-user")}
                spreadBehaviour="free"
                type="danger"
            />
        </View>
        <MemberEditModal
            onClose={() => setIsEditModalVisible(false)}
            onSuccess={() => {
                setIsEditModalVisible(false);
                fetchMemberDetail();
            }}
            isVisible={isEditModalVisible}
            memberData={memberData}
        />
        <Modal
            onClosed={() => {
                setIsDeleteDialogOpen(false);
                setDeleteConfirmationText("");
                setIsBlacklistEnabled(false);
                setBlacklistReason("");
            }}
            isActive={isDeleteDialogOpen}
            portalName="modal-system"
            id="member-delete-modal"
        >
            <View
                style={{
                    backgroundColor: colors.project.brandWhite,
                    width: isMobile ? "90%" : 400,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingXl,
                    gap: spaces.spacingXl,
                    alignSelf: "center"
                }}
            >
                <Text
                    variant="headlineMediumSize"
                >
                    {localize("members-detail-delete-confirm-title") || "Emin misiniz?"}
                </Text>
                <View>
                    <Text
                        style={{
                            marginBottom: spaces.spacingMd
                        }}
                        variant="bodyMediumSize"
                    >
                        {localize("members-detail-delete-confirm-desc") || "Bu kullanıcıyı kalıcı olarak silmek istediğinize emin misiniz?"}
                    </Text>
                    <TextInput
                        placeholder={localize("members-delete-confirm-placeholder")}
                        onChangeText={setDeleteConfirmationText}
                        value={deleteConfirmationText}
                        spreadBehaviour="stretch"
                    />
                    <View
                        style={{
                            justifyContent: "space-between",
                            marginTop: spaces.spacingMd,
                            flexDirection: "row",
                            alignItems: "center"
                        }}
                    >
                        <Text
                            variant="bodyMediumSize"
                        >
                            {localize("members-detail-delete-and-blacklist") || "Üyeyi kara listeye ekle"}
                        </Text>
                        <Switch
                            onPress={() => setIsBlacklistEnabled(!isBlacklistEnabled)}
                            customTheme={{
                                themeKey: "events_form" as never
                            }}
                            isActive={isBlacklistEnabled}
                            type="neutral"
                        />
                    </View>
                    {isBlacklistEnabled && (
                        <View
                            style={{
                                marginTop: spaces.spacingMd
                            }}
                        >
                            <TextInput
                                placeholder={localize("members-detail-blacklist-reason-placeholder") || "Kara listeye alma nedeninizi yazın (Opsiyonel)"}
                                title={localize("members-detail-blacklist-reason-title") || "Neden"}
                                onChangeText={setBlacklistReason}
                                spreadBehaviour="stretch"
                                value={blacklistReason}
                                multiline={true}
                            />
                        </View>)}
                </View>
                <View
                    style={{
                        justifyContent: "flex-end",
                        flexDirection: "row",
                        gap: spaces.spacingMd
                    }}
                >
                    <Button
                        title={localize("members-detail-cancel") || "İptal"}
                        customBorderColor="secondaryButton"
                        onPress={() => {
                            setIsDeleteDialogOpen(false);
                            setDeleteConfirmationText("");
                            setIsBlacklistEnabled(false);
                            setBlacklistReason("");
                        }}
                        customColor="secondaryButton"
                        variant="filled"
                    />
                    <Button
                        isDisabled={deleteConfirmationText.toLocaleUpperCase("tr-TR") !== "SİL"}
                        title={localize("members-detail-delete") || "Sil"}
                        onPress={() => {
                            setIsDeleteDialogOpen(false);
                            setDeleteConfirmationText("");
                            setIsBlacklistEnabled(false);
                            setBlacklistReason("");
                            handleDeleteUser();
                        }}
                        type="danger"
                    />
                </View>
            </View>
        </Modal>
    </PageContainer>;
};

export default MemberDetail;
